/** Sliding board gate inside a fixed frame. A single water aperture stays faceless at every lift. */
import { checkEnvelope, checkScope, el, n, partGroup } from '../../svg';
import type { PieceArt, PieceOpening, PieceRecipe, ResolvedPalette } from '../../contract';
import { amount, beam, grainRng, ground, line, rivet } from './machinerySvg';
export interface SluiceGateParams { readonly open: number; readonly copper: number }
export type SluiceGateRecipe = PieceRecipe<'mill.sluice_gate.v1', SluiceGateParams>;
export const SLUICE_GATE_FRAME = { w: 120, h: 150 } as const;
export const MILL_SLUICE_GATE: SluiceGateRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'mill.sluice_gate.default.v1', generatorId: 'mill.sluice_gate.v1',
  seed: 20071005, paletteId: 'millrace_amber', params: { open: 0.35, copper: 0.75 },
};
export function buildSluiceGate(recipe: SluiceGateRecipe, p: ResolvedPalette, scope: string): PieceArt {
  checkEnvelope(recipe, MILL_SLUICE_GATE.generatorId); checkScope(scope);
  const open = amount(recipe.params.open, 'open'), copper = amount(recipe.params.copper, 'copper');
  const random = grainRng(recipe), parts: string[] = [], bottom = 137 - open * 49;
  const openings: PieceOpening[] = open > 0 ? [{ x: 28, y: bottom, width: 64, height: 137 - bottom, fill: p.waterFar }] : [];
  const masonry = ground(120, 150, p) + el('path', { d: 'M9 145V73L22 66L26 145ZM94 145L98 66L112 72V145Z', fill: p.stoneBase, stroke: p.outline, 'stroke-width': 1.5 })
    + line(11, 102, 24, 100, p.stoneShadow, 1.5) + line(97, 117, 110, 119, p.stoneShadow, 1.5)
    + el('rect', { x: 28, y: 61, width: 64, height: 76, fill: p.waterFar })
    + el('path', { d: 'M26 138H94L99 145H21Z', fill: p.stoneLight });
  let boards = '';
  for (let i = 0; i < 5; i++) {
    const y = bottom - 62 + i * 12.4;
    boards += el('rect', { x: 28, y, width: 64, height: 12.4, fill: i % 2 ? p.woodBase : p.woodLight, stroke: p.woodShadow, 'stroke-width': 1 })
      + line(34 + random(`start.${i}`) * 4, y + 7, 84, y + 6 + random(`grain.${i}`) * 3, p.woodShadow, 0.7);
  }
  boards += el('path', { d: `M37 ${n(bottom - 63)}H43V${n(bottom - 1)}H37ZM77 ${n(bottom - 63)}H83V${n(bottom - 1)}H77Z`, fill: p.paperShadow })
    + el('path', { d: `M38 ${n(bottom - 63)}H41V${n(bottom - 1)}H38ZM78 ${n(bottom - 63)}H81V${n(bottom - 1)}H78Z`, fill: p.paperBase, opacity: copper });
  const frame = beam(23, 137, 23, 31, 9, p) + beam(97, 137, 97, 31, 9, p) + beam(18, 32, 102, 32, 10, p)
    + line(60, 20, 60, bottom - 63, p.stoneShadow, 5)
    + line(59, 21, 59, bottom - 63, p.paperBase, 1.5)
    + rivet(23, 33, p) + rivet(97, 33, p);
  let winch = el('circle', { cx: 60, cy: 18, r: 14, fill: 'none', stroke: p.outline, 'stroke-width': 4 })
    + el('circle', { cx: 60, cy: 18, r: 14, fill: 'none', stroke: p.paperBase, 'stroke-width': 2 });
  for (let i = 0; i < 3; i++) {
    const angle = (i * Math.PI * 2) / 3;
    winch += line(60, 18, 60 + Math.cos(angle) * 12, 18 + Math.sin(angle) * 12, p.paperShadow, 3);
  }
  winch += rivet(60, 18, p, 3) + el('circle', { cx: 70, cy: 9, r: 2.5, fill: p.woodLight, stroke: p.outline, 'stroke-width': 1 });
  const body = partGroup(parts, 'sluice-masonry', masonry) + partGroup(parts, 'sluice-board-stack', boards)
    + partGroup(parts, 'sluice-frame', frame) + partGroup(parts, 'sluice-winch', winch);
  return { frame: SLUICE_GATE_FRAME, body, anchors: { support: { x: 60, y: 150 }, winch: { x: 60, y: 18 } }, parts, openings };
}
